import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-hot-toast'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation } from '@/components/StickyHeaderNavigation'
import { TextInputForm } from '@/components/form/TextInputForm'
import { AppButton } from '@/components/AppButton'
import { Info } from '@/assets/icons'
import { useUpdateUserKycInfo } from '@/domain/identity/useCases'
import { useUpdateLegalNameSchema, IUpdateLegalNameSchema } from '@/schemas'
import { inputLimits } from '@/constants/inputLimits'
import useOffsets from '@/hooks/useOffsets'
import { useCheckTransactionEligibility } from '@/hooks'

export interface UpdateLegalNameParams {
  firstName: string
  lastName: string
}

export const UpdateLegalNameScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offsets = useOffsets()
  const [searchParams] = useSearchParams()

  // Get parameters from URL
  const firstName = searchParams.get('firstName') || ''
  const lastName = searchParams.get('lastName') || ''

  const schema = useUpdateLegalNameSchema()

  const { control, handleSubmit, setFocus } = useForm<IUpdateLegalNameSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: firstName,
      lastName: lastName,
    },
    mode: 'onChange',
  })

  const { checkTransactionEligibility } = useCheckTransactionEligibility({
    feature: 'card',
  })

  const { mutateAsync: updateUserKycInfo, isPending: isUpdatingUserKycInfo } =
    useUpdateUserKycInfo({
      onSuccess: () => {
        toast.success(t('form:success.changesSaved'))
        navigate(-1)
      },
      onError: ({ errorMessage }) => {
        toast.error(errorMessage ?? t('api:errors.somethingWrong'))
      },
    })

  const handleSubmitForm = async (data: IUpdateLegalNameSchema) => {
    // Check eligibility before updating the name
    const isEligible = await checkTransactionEligibility()

    if (!isEligible) {
      return
    }

    await updateUserKycInfo({
      first_name: data.firstName,
      last_name: data.lastName,
    })
  }

  return (
    <>
      {/* Sticky Header Navigation */}
      <StickyHeaderNavigation
        title={t('screens:confirmYourLegalName.title')}
        onBackPress={() => navigate(-1)}
      />

      <BaseView
        flex={1}
        px={12}
        style={{
          backgroundColor: '#131415',
          minHeight: '100vh',
          paddingTop: offsets.top + 50, // Space for sticky header
          paddingBottom: offsets.bottom + 80,
        }}
      >
        {/* Form Fields */}
        <BaseView gap={12} mt={24}>
          <TextInputForm
            control={control}
            name="firstName"
            label={t('form:fields.firstName')}
            maxLength={inputLimits.firstName.max}
            autoCapitalize="words"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setFocus('lastName')
              }
            }}
          />

          <TextInputForm
            control={control}
            name="lastName"
            label={t('form:fields.lastName')}
            maxLength={inputLimits.lastName.max}
            autoCapitalize="words"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(handleSubmitForm)()
              }
            }}
          />

          {/* Alert Message */}
          <BaseView
            row
            gap={12}
            p={12}
            style={{
              borderRadius: '16px',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
            }}
          >
            <Info size={20} color="#F59E0B" />

            <BaseText
              style={{
                flex: 1,
                flexWrap: 'wrap',
                color: 'white',
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              {t('alerts:updateLegalNameAlertMessage')}
            </BaseText>
          </BaseView>
        </BaseView>

        {/* Submit Button */}
        <BaseView mt="auto">
          <AppButton
            label={t('form:actions.confirm')}
            onPress={handleSubmit(handleSubmitForm)}
            isLoading={isUpdatingUserKycInfo}
          />
        </BaseView>
      </BaseView>
    </>
  )
}
